/**
 * services/SyncSettingsService.ts
 * Service for managing IndexedDB sync settings cache + device-specific state
 *
 * Architecture (Task 2.6 - Option A.1):
 * - IndexedDB stores CACHE of server settings for offline access
 * - IndexedDB stores device-specific directoryId (source of truth)
 * - Source of truth for universal prefs: Supabase user_sync_settings
 *
 * Usage:
 * - Internal use by UserSyncSettingsService for cache management
 * - Direct use for directoryId updates (device-specific, never synced to server)
 * - DO NOT use directly for universal preferences (use UserSyncSettingsService instead)
 */

import { BaseService } from './BaseService';
import type { SyncSettings, ConflictResolutionStrategy } from '@/types/sync/SyncSettings';
import { DEFAULT_SYNC_SETTINGS } from '@/types/sync/SyncSettings';
import type { SyncProviderType } from '@/types/sync/SyncProvider';

/**
 * Service for managing IndexedDB sync settings
 *
 * Role (Option A.1):
 * 1. Cache management: Store last-known-good server settings for offline access
 * 2. Device-specific state: Manage directoryId for local provider
 *
 * IMPORTANT: For universal preferences (activeSyncProvider, syncEnabled, etc.),
 * use UserSyncSettingsService which manages Supabase source of truth + this cache.
 * Only use this service directly for directoryId updates or internal cache operations.
 */
export class SyncSettingsService extends BaseService<SyncSettings> {
  constructor() {
    super('sync_settings'); // Links to db.sync_settings table
  }

  // ===== INITIALIZATION =====

  /**
   * Get existing sync settings or create default settings if none exist
   *
   * Typically there will be only one SyncSettings record per user.
   *
   * @returns Promise resolving to sync settings
   */
  async getOrCreateDefault(): Promise<SyncSettings> {
    const allSettings = await this.getAll();

    // If settings already exist, return the first one
    if (allSettings.length > 0) {
      const settings = allSettings[0];
      if (!settings) {
        throw new Error('Failed to retrieve sync settings');
      }
      return settings;
    }

    // Create default settings
    const defaultSettings = await this.add(DEFAULT_SYNC_SETTINGS);
    console.log('✅ Created default sync settings');
    return defaultSettings;
  }

  // ===== SYNC ENABLED/DISABLED =====

  /**
   * Enable or disable automatic background sync
   *
   * @param enabled - Whether sync should be enabled
   * @returns Promise resolving to updated settings
   */
  async updateSyncEnabled(enabled: boolean): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { syncEnabled: enabled });
  }

  // ===== SYNC INTERVAL =====

  /**
   * Update the sync interval (in minutes)
   *
   * @param minutes - Sync interval (1, 5, 10, 15, 30)
   * @returns Promise resolving to updated settings
   */
  async updateSyncInterval(minutes: number): Promise<SyncSettings> {
    // Validate interval is one of the allowed values
    const allowedIntervals = [1, 5, 10, 15, 30];
    if (!allowedIntervals.includes(minutes)) {
      throw new Error(
        `Invalid sync interval: ${minutes}. Must be one of: ${allowedIntervals.join(', ')}`
      );
    }

    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { syncInterval: minutes });
  }

  // ===== SYNC TRIGGERS =====

  /**
   * Enable or disable sync on session close
   *
   * @param enabled - Whether to sync when closing app/tab
   * @returns Promise resolving to updated settings
   */
  async updateSyncOnSessionClose(enabled: boolean): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { syncOnSessionClose: enabled });
  }

  /**
   * Enable or disable sync on login
   *
   * @param enabled - Whether to sync when logging in
   * @returns Promise resolving to updated settings
   */
  async updateSyncOnLogin(enabled: boolean): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { syncOnLogin: enabled });
  }

  // ===== PROVIDER CONFIGURATION =====

  /**
   * Set the active sync provider
   *
   * @param provider - Sync provider type or null to disable
   * @returns Promise resolving to updated settings
   */
  async updateActiveSyncProvider(provider: SyncProviderType | null): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { activeSyncProvider: provider });
  }

  // ===== CONFLICT RESOLUTION =====

  /**
   * Update the conflict resolution strategy
   *
   * @param strategy - Conflict resolution strategy
   * @returns Promise resolving to updated settings
   */
  async updateConflictResolution(
    strategy: ConflictResolutionStrategy
  ): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, { conflictResolution: strategy });
  }

  // ===== BULK UPDATES =====

  /**
   * Update multiple sync settings at once
   *
   * @param updates - Partial sync settings to update
   * @returns Promise resolving to updated settings
   */
  async updateSettings(updates: Partial<SyncSettings>): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, updates);
  }

  // ===== RESET =====

  /**
   * Reset sync settings to defaults
   *
   * @returns Promise resolving to reset settings
   */
  async resetToDefaults(): Promise<SyncSettings> {
    const settings = await this.getOrCreateDefault();
    return await this.update(settings.id, DEFAULT_SYNC_SETTINGS);
  }
}
